import React, { ComponentProps, useRef, useState } from 'react'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import TableHeaders from '../Interfaces/TableHeaders'
import { useTheme } from '@mui/material/styles'
import { dateToSlashStringReinterpretedAsLocal } from '../../utils/dateUtility'
import { useNavigate } from 'react-router'
import TableHeader from '../Table/TableHeader'
import SearchBar from '../Search/SearchBar'
import NoResultsFound from '../Table/NoResultsFound'
import { EnrollmentInviteStatusEnum } from '../../swagger'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import TextButton, { TextButtonVariant } from '../Buttons/TextButton'
import { ContainedButtonVariant } from '../Buttons/ContainedButton'
import { SnackbarSeverity } from '../Alerts/SnackbarAlert'
import { extractedErrorObject, families } from '../../api/swagger'
import ConfirmationModal from '../Modals/ConfirmationModal'
import ActionButtons from '../Buttons/ActionButtons'
import TableFooterPagination, {
  defaultPageSize,
} from '../Pagination/TableFooterPagination'
import EnrollmentInviteRowInfo from '../Interfaces/EnrollmentInviteRowInfo'
import DropDown, { DropDownVariant } from '../Menus/DropDown'
import { useSnackbarContext } from '../Context/SnackbarContext'
import { validateEmailText } from '../../helpers/validateEmail'
import useEmailMessage from '../../hooks/useEmailMessage'
import Box from '@mui/material/Box'
import { styled } from '@mui/system'
import { useAuth } from '../Routes/AuthProvider'
import {
  ENROLLMENT_INVITES_MIN_SEARCH_LENGTH,
  enrollmentInviteStatusFilterOptions,
  useProgramDetailsContext,
} from '../Context/ProgramDetailsContext'

const Section = styled('section')(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}))

const ItalicSpan = styled('span')({
  fontStyle: 'italic',
})

const ClickableCell = ({
  children,
  onClick,
  withPointer = true,
}: ComponentProps<typeof TableCell> & { withPointer?: boolean }) => (
  <TableCell
    onClick={onClick}
    sx={{
      cursor: withPointer ? 'pointer' : 'default',
    }}
  >
    {children}
  </TableCell>
)

enum ConfirmationModalVariant {
  Resend,
  Cancel,
}

export interface InputProperties {
  value: string
  error: string
  valid: boolean
  initialValue: string
}

interface EnrollmentsInviteTableProps {
  enrollmentsInvite?: EnrollmentInviteRowInfo[]
  refetchEnrollmentsData?: () => void
}

export const EnrollmentsInviteTable: React.FC<EnrollmentsInviteTableProps> = ({
  enrollmentsInvite,
  refetchEnrollmentsData,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { validationMessages } = useEmailMessage()
  const { setSnackbarSeverity, setSnackbarMessage, setSnackbarState } =
    useSnackbarContext()
  const { permissionAbility } = useAuth()
  const navigate = useNavigate()
  const showOnDesktop = useShowOnDesktop()
  const familyNameActingOn = useRef('')
  const invitationIdActingOn = useRef('')

  const InitialStateSentEmail: InputProperties = {
    value: '',
    error: '',
    valid: false,
    initialValue: '',
  }

  /** State Objects */
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [confirmationModalVariant, setConfirmationModalVariant] = useState(
    ConfirmationModalVariant.Resend
  )

  const [sentEmail, setSentEmail] = useState<InputProperties>(
    InitialStateSentEmail
  )

  const {
    handleEnrollmentInvitesPageChange,
    handleEnrollmentInvitesPageSizeChange,
    updateEnrollmentInviteFilters,
    updateSearchEnrollmentInvites,

    enrollmentInviteFilters,
    enrollmentInvitesPagination,
    isSearchingOrFilteringEnrollmentInvites,
  } = useProgramDetailsContext()

  const getLabelForInviteStatus = (options: {
    inviteStatus?: string
  }): string => {
    let label = ''
    switch (options.inviteStatus) {
      case enrollmentInviteStatusFilterOptions.AllStatus:
      default:
        label = t(
          'EnrollmentsInvite.FilterOption.AllInviteStatus',
          'All Invite Statuses'
        )
        break
      case enrollmentInviteStatusFilterOptions.InProgress:
        label = t('EnrollmentsInvite.FilterOption.InProgress', 'In Progress')
        break
      case enrollmentInviteStatusFilterOptions.Complete:
        label = t('EnrollmentsInvite.FilterOption.Complete', 'Accepted')
        break
      case enrollmentInviteStatusFilterOptions.Cancelled:
        label = t('EnrollmentsInvite.FilterOption.Cancelled', 'Cancelled')
        break
    }
    return label
  }

  /** Other Objects */
  const tableHeaders: TableHeaders[] = [
    {
      label: t('Enrollments.Invite.FamilyName.Header', 'Family Name'),
      align: 'left',
    },
    {
      label: t('Enrollments.Invite.SentEmail.Header', 'Sent Email'),
      align: 'left',
    },
    {
      label: t('Enrollments.Invite.InviteDate.Header', 'Invite Date'),
      align: 'left',
    },
    {
      label: t('Enrollments.Invite.OfferedSpot.Header', 'Offered Spots'),
      align: 'left',
    },
    {
      label: t('Enrollments.Invite.SkippedSpots.Header', 'Skipped Spots'),
      align: 'left',
    },
    {
      label: t('Enrollments.Invite.InviteStatus.Header', 'Status'),
      align: 'left',
    },
    {
      label: t('Enrollments.Invite.InviteActions.Header', ''),
      id: 'resendCancel',
      align: 'left',
    },
  ]

  /** Methods */
  const navigateToFamilyProfile = (userId: number) => {
    navigate(
      {
        pathname: `/family-profile/${userId.toString()}`,
      },
      {
        /** Navigation Options */
      }
    )
  }

  const handleSearch = (searchText: string) => {
    handleEnrollmentInvitesPageChange?.(0)
    if (searchText.length >= ENROLLMENT_INVITES_MIN_SEARCH_LENGTH) {
      updateSearchEnrollmentInvites(searchText)
    }
    if (searchText.length === 0) {
      updateSearchEnrollmentInvites('')
    }
  }

  const handleResendInvitation = async (
    event: React.FormEvent<HTMLDivElement>
  ) => {
    event.preventDefault()
    let snackbarMessage: string
    let snackbarSeverity: SnackbarSeverity

    try {
      await families.resendInvitation({
        enrollmentInviteUuid: invitationIdActingOn.current,
        body:
          sentEmail.initialValue === sentEmail.value
            ? {}
            : { sentToEmail: sentEmail.value },
      })
      refetchEnrollmentsData?.()
      const successMessage = t(
        'EnrollmentsInviteTable.ResendInvitation',
        'Invitation resent'
      )
      snackbarMessage = successMessage
      snackbarSeverity = SnackbarSeverity.Success
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown',
        message: (e as unknown as Error).message,
      }
      snackbarMessage = errorObject.message
      snackbarSeverity = SnackbarSeverity.Error
    }
    setSnackbarState?.(true)
    setSnackbarMessage?.(snackbarMessage)
    setSnackbarSeverity?.(snackbarSeverity)

    setShowConfirmationModal(false)
  }

  const sharedDropDownProps = {
    variant: DropDownVariant.SortAndFilter,
    fullWidth: !showOnDesktop,
  }

  const handleCancelInvitation = async (
    event: React.FormEvent<HTMLDivElement>
  ) => {
    event.preventDefault()
    try {
      await families.cancelInvitation({ uuid: invitationIdActingOn.current })
      refetchEnrollmentsData?.()
      const successMessage = t(
        'EnrollmentsInviteTable.CancelInvitation',
        'Invitation cancelled'
      )
      setSnackbarState?.(true)
      setSnackbarMessage?.(successMessage)
      setSnackbarSeverity?.(SnackbarSeverity.Success)
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown',
        message: (e as unknown as Error).message,
      }
      setSnackbarState?.(true)
      setSnackbarMessage?.(errorObject.message)
      setSnackbarSeverity?.(SnackbarSeverity.Error)
    } finally {
      setShowConfirmationModal(false)
    }
  }

  const handleConfirmationCancel = async () => {
    setShowConfirmationModal(false)
    setSentEmail(InitialStateSentEmail)
  }

  const handleInviteStatusFilterSelection = (
    _: string,
    __: string,
    selectionOptionId: string | number
  ) => {
    handleEnrollmentInvitesPageChange?.(0)
    updateEnrollmentInviteFilters({
      ...enrollmentInviteFilters,
      inviteStatus: `${selectionOptionId}`,
    })
  }

  const handleChangeSentEmail = (email: string) => {
    email = email.trim()
    const errorEmailCode = validateEmailText(email)

    setSentEmail({
      ...sentEmail,
      value: email,
      error: validationMessages(errorEmailCode),
      valid: !!errorEmailCode,
    })
  }

  const resendMessage = t(
    'EnrollmentsInviteTable.ConfirmationModal.Header.Resend',
    'Resend'
  )
  const cancelMessage = t(
    'EnrollmentsInviteTable.ConfirmationModal.Header.Cancel',
    'Cancel'
  )
  const baseConfirmationHeaderMessage = `${t(
    'EnrollmentsInviteTable.ConfirmationModal.Header.Base',
    'this invitation to'
  )} ${familyNameActingOn.current}?`

  const confirmationBody = (
    <>
      <Typography
        variant="body1"
        component="p"
        align="center"
        margin={theme.spacing(1)}
      >
        {t(
          'EnrollmentsInviteTable.ConfirmationModal.Body.Resend.Invitation',
          'This will resend the same invitation to the family.'
        )}
      </Typography>
      <Typography
        variant="body1"
        component="p"
        align="center"
        margin={theme.spacing(1)}
      >
        {t(
          'EnrollmentsInviteTable.ConfirmationModal.Body.Resend.ReviewEmail',
          'Please review and/or modify the email address below to ensure it is correct.'
        )}
      </Typography>
      <Typography
        variant="body1"
        component="p"
        align="center"
        margin={theme.spacing(1)}
      >
        {t(
          'EnrollmentsInviteTable.ConfirmationModal.Body.Resend.Label',
          'For '
        )}
        <ItalicSpan>
          {t(
            'EnrollmentsInviteTable.ConfirmationModal.Body.Resend.Span',
            'returning families, '
          )}
        </ItalicSpan>
        {t(
          'EnrollmentsInviteTable.ConfirmationModal.Body.Resend.CCEmail',
          'please ensure the email address matches their CC Connected account.'
        )}
      </Typography>
    </>
  )

  const resendConfirmationProps = {
    isOpen: showConfirmationModal,
    dialogTitle: `${resendMessage} ${baseConfirmationHeaderMessage}`,
    dialogContent: confirmationBody,
    dialogActions: (
      <ActionButtons
        primaryButtonLabel={ContainedButtonVariant.YesResend}
        secondaryButtonLabel={TextButtonVariant.NoClose}
        secondaryClick={handleConfirmationCancel}
      />
    ),
    handleFormSubmit: handleResendInvitation,
    inputProperties: sentEmail,
    handleInputChange: handleChangeSentEmail,
    viewEntrySentToEmail: true,
    nameInput: 'Sent Email',
  }

  const cancelConfirmationProps = {
    isOpen: showConfirmationModal,
    dialogTitle: `${cancelMessage} ${baseConfirmationHeaderMessage}`,
    dialogContent: (
      <Typography variant="body1" component="p" align="center">
        {t(
          'EnrollmentsInviteTable.ConfirmationModal.Body.Cancel',
          'Are you sure you wish to cancel invitation? All programs associated with this invitation will be cancelled. Do you wish to continue?'
        )}
      </Typography>
    ),
    dialogActions: (
      <ActionButtons
        primaryButtonLabel={ContainedButtonVariant.YesCancel}
        secondaryButtonLabel={TextButtonVariant.NoClose}
        secondaryClick={handleConfirmationCancel}
      />
    ),
    handleFormSubmit: handleCancelInvitation,
  }

  const handleResendClick = (it: EnrollmentInviteRowInfo) => {
    setConfirmationModalVariant(ConfirmationModalVariant.Resend)
    setShowConfirmationModal(true)
    familyNameActingOn.current = it.familyName
    invitationIdActingOn.current = it.rowId
    setSentEmail({
      ...sentEmail,
      value: it.sentToEmail,
      initialValue: it.sentToEmail,
    })
  }

  const confirmationProps =
    confirmationModalVariant === ConfirmationModalVariant.Resend
      ? resendConfirmationProps
      : cancelConfirmationProps

  const handleClickChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const updatedRowsPerPage = parseInt(event.target.value, 10)
    handleEnrollmentInvitesPageSizeChange?.(updatedRowsPerPage)
  }

  const handleClickChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    handleEnrollmentInvitesPageChange?.(newPage)
  }

  return (
    <>
      <ConfirmationModal {...confirmationProps} />
      <Section aria-label="searchSortAndFilter">
        <SearchBar handleSearch={handleSearch} />
        <Box
          sx={{
            marginLeft: theme.spacing(2),
            [theme.breakpoints.down('sm')]: {
              marginLeft: theme.spacing(0),
              marginBottom: theme.spacing(2),
              width: '100%',
            },
          }}
        >
          <DropDown
            formControlProps={{
              [theme.breakpoints.down('sm')]: {
                margin: theme.spacing(0),
                width: '100%',
              },
            }}
            id="inviteStatusFilter"
            menuOptions={Object.values(enrollmentInviteStatusFilterOptions).map(
              (status) => {
                return {
                  name: getLabelForInviteStatus({ inviteStatus: status }),
                  id: status,
                }
              }
            )}
            defaultValue={getLabelForInviteStatus({
              inviteStatus: enrollmentInviteFilters.inviteStatus,
            })}
            value={getLabelForInviteStatus({
              inviteStatus: enrollmentInviteFilters.inviteStatus,
            })}
            handleSelection={handleInviteStatusFilterSelection}
            {...{ ...sharedDropDownProps, fullWidth: false }}
          />
        </Box>
      </Section>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableHeader tableHeaders={tableHeaders} />
          </TableHead>
          <TableBody>
            {enrollmentsInvite &&
              enrollmentsInvite.length > 0 &&
              enrollmentsInvite.map((enrollmentInvite) => {
                const isFamilyAccountCreatedAndCompletedInvite =
                  !!enrollmentInvite.primaryContactId &&
                  enrollmentInvite.enrollmentInviteStatus ===
                    EnrollmentInviteStatusEnum.Complete
                const onTableCellClick = () => {
                  !!enrollmentInvite.primaryContactId &&
                    enrollmentInvite.enrollmentInviteStatus ===
                      EnrollmentInviteStatusEnum.Complete &&
                    navigateToFamilyProfile(enrollmentInvite.primaryContactId)
                }
                return (
                  <TableRow key={enrollmentInvite.rowId}>
                    <ClickableCell
                      onClick={onTableCellClick}
                      withPointer={isFamilyAccountCreatedAndCompletedInvite}
                    >
                      <Typography variant="body1" component="p">
                        {enrollmentInvite.familyName}
                      </Typography>
                    </ClickableCell>
                    <ClickableCell
                      onClick={onTableCellClick}
                      withPointer={isFamilyAccountCreatedAndCompletedInvite}
                    >
                      <Typography variant="body1" component="p">
                        {enrollmentInvite.sentToEmail}
                      </Typography>
                    </ClickableCell>
                    <ClickableCell
                      onClick={onTableCellClick}
                      withPointer={isFamilyAccountCreatedAndCompletedInvite}
                    >
                      <Typography variant="body1" component="p">
                        {dateToSlashStringReinterpretedAsLocal(
                          enrollmentInvite.inviteDate
                        )}
                      </Typography>
                    </ClickableCell>
                    <ClickableCell
                      onClick={onTableCellClick}
                      withPointer={isFamilyAccountCreatedAndCompletedInvite}
                    >
                      <Typography variant="body1" component="p">
                        {enrollmentInvite.offeredSpots}
                      </Typography>
                    </ClickableCell>
                    <ClickableCell
                      onClick={onTableCellClick}
                      withPointer={isFamilyAccountCreatedAndCompletedInvite}
                    >
                      <Typography variant="body1" component="p">
                        {enrollmentInvite.skippedSpots}
                      </Typography>
                    </ClickableCell>
                    <ClickableCell
                      onClick={onTableCellClick}
                      withPointer={isFamilyAccountCreatedAndCompletedInvite}
                    >
                      <Typography variant="body1" component="p">
                        {getLabelForInviteStatus({
                          inviteStatus: enrollmentInvite.enrollmentInviteStatus,
                        })}
                      </Typography>
                    </ClickableCell>
                    <TableCell>
                      {enrollmentInvite.enrollmentInviteStatus ===
                      EnrollmentInviteStatusEnum.InProgress ? (
                        <>
                          <TextButton
                            id="EditAndResend"
                            onClick={() => handleResendClick(enrollmentInvite)}
                            variant={TextButtonVariant.EditAndResend}
                          />
                          {permissionAbility.can(
                            'allowCancelInvite',
                            'Family'
                          ) && (
                            <TextButton
                              id="cancel"
                              onClick={() => {
                                setConfirmationModalVariant(
                                  ConfirmationModalVariant.Cancel
                                )
                                setShowConfirmationModal(true)
                                familyNameActingOn.current =
                                  enrollmentInvite.familyName
                                invitationIdActingOn.current =
                                  enrollmentInvite.rowId
                              }}
                              variant={TextButtonVariant.CancelInvitation}
                            />
                          )}
                        </>
                      ) : (
                        []
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}

            {isSearchingOrFilteringEnrollmentInvites &&
              enrollmentsInvite?.length === 0 && <NoResultsFound />}
          </TableBody>
          {enrollmentInvitesPagination &&
          enrollmentInvitesPagination.totalCount > defaultPageSize ? (
            <TableFooterPagination
              count={enrollmentInvitesPagination.totalCount}
              onChangePage={handleClickChangePage}
              onChangeRowsPerPage={handleClickChangePageSize}
              page={enrollmentInvitesPagination.page - 1}
              rowsPerPage={enrollmentInvitesPagination.pageSize}
              colSpan={tableHeaders.length}
            />
          ) : null}
        </Table>
      </TableContainer>
    </>
  )
}

export default EnrollmentsInviteTable
